import {
  type AppError,
  BadRequestError,
  ForbiddenError,
  InvalidTokenError,
  NetworkError,
  NotFoundError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {filterExistParams} from '../../shared/services/filterExistParams';
import {
  RECRUITMENT_ORDER,
  RECRUITMENT_TAGS,
} from '../entities/recruitment/constants';
import {
  type GetBrowsingHistoryRecommendListResponse,
  GetBrowsingHistoryRecommendListResponseSchema,
  type GetEntryReadyRecommendsResponse,
  GetEntryReadyRecommendsResponseSchema,
  type GetEntrySimilarRecommendsResponse,
  GetEntrySimilarRecommendsResponseSchema,
  type GetEntrySimilarRecruitmentListResponse,
  GetEntrySimilarRecruitmentListResponseScheme,
  type GetRecommendRecruitmentListResponse,
  GetRecommendRecruitmentListResponseSchema,
  type GetRecommendedRecruitmentListResponse,
  GetRecommendedRecruitmentListResponseSchema,
  type GetRecommendedRecruitsByIndustryResponse,
  GetRecommendedRecruitsByIndustrySchema,
  GetRecruitmentIdByDetailIdResponseSchema,
  type GetRecruitmentListResponse,
  type GetRecruitmentWebSeminarsResponse,
  GetRecruitmentWebSeminarsResponseSchema,
  type GetSimilarRecruitmentListResponse,
  GetSimilarRecruitmentListResponseScheme,
  type GetUnfinishedEntryPromptResponse,
  GetUnfinishedEntryPromptResponseSchema,
  type InternalPreviewCodeCurrentUnivType,
  type RecruitmentDetailResponse,
  RecruitmentDetailResponseSchema,
  RecruitmentListResponseSchema,
  type RelatedCompanyRecruitmentListResponse,
  RelatedCompanyRecruitmentListResponseSchema,
} from '../entities/recruitment/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

/**
 * GetRecruitmentItemIdByDetailId
 * `gs_recruiting_items.detail_id`とadmin/clientの値から`gs_recruiting_items.id`を取得する
 */
type getRecruitmentIdByDetailIdParam = {
  cookies: Partial<Record<string, string>>;
  presenter: 'admin' | 'client';
  detailId: number;
  version?: string;
};

export const getRecruitmentIdByDetailId = async ({
  cookies,
  presenter,
  detailId,
  version = DEFAULT_API_VERSION,
}: getRecruitmentIdByDetailIdParam) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    return defaultClient.get(
      `/${version}/recruitment-detail/${presenter}/${detailId}`,
      GetRecruitmentIdByDetailIdResponseSchema,
    );
  };

  const result = await sendApiRequest({
    request,
    authRequired: false,
    cookies,
    version,
    allowReturnError: true,
  });

  return result;
};

/**
 * GetRecruitmentList
 */
export const GetRecruitmentOrderType = z.union([
  z.literal(RECRUITMENT_ORDER.RECOMMEND),
  z.literal(RECRUITMENT_ORDER.RECENT),
  z.literal(RECRUITMENT_ORDER.DEADLINE),
  z.literal(RECRUITMENT_ORDER.EVENT),
]);
export type GetRecruitmentOrderType = z.infer<typeof GetRecruitmentOrderType>;

export const GetRecruitmentTagsType = z.union([
  z.literal(RECRUITMENT_TAGS.OVERSEAS_STUDENT),
  z.literal(RECRUITMENT_TAGS.SCIENCE_STUDENT),
  z.literal(RECRUITMENT_TAGS.ENGINEER),
]);
export type GetRecruitmentTagsType = z.infer<typeof GetRecruitmentTagsType>;

type GetRecruitmentListParam = {
  order?: GetRecruitmentOrderType;
  companyIDs?: number[];
  industryCategoryIDs?: number[];
  groupedJobTypeIDs?: number[];
  gy?: number;
  allgy?: 'true' | 'false';
  typeIDs?: string[];
  tagIDs?: GetRecruitmentTagsType[];
  page?: number;
  limit?: number;
  isShuffle?: 'true' | 'false';
  // 日付フィルター
  eventStart?: string;
  eventEnd?: string;
  entryStart?: string;
  entryEnd?: string;
};
export type IGetRecruitmentList = GetRecruitmentListParam & {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type GetRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecruitmentListResponse,
  AppError
>;

export const getRecruitmentList = async ({
  cookies,
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  page,
  limit,
  isShuffle,
  eventStart,
  eventEnd,
  entryStart,
  entryEnd,
  version = DEFAULT_API_VERSION,
}: IGetRecruitmentList) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment`,
      {
        order: order ?? RECRUITMENT_ORDER.RECENT,
        companyIDs: companyIDs ?? '',
        industryCategoryIDs: industryCategoryIDs ?? '',
        groupedJobTypeIDs: groupedJobTypeIDs ?? '',
        gy,
        allgy: allgy ?? 'false',
        typeIDs: typeIDs ?? '',
        tagIDs: tagIDs ?? '',
        page: page ?? 1,
        limit: limit ?? 10,
        isShuffle: isShuffle ?? 'false',
        eventStart: eventStart ?? '',
        eventEnd: eventEnd ?? '',
        entryStart: entryStart ?? '',
        entryEnd: entryEnd ?? '',
      },
      'none',
    );
    return defaultClient.get(path, RecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type GetRecruitmentListOptions = {
  config?: Omit<
    UseQueryOptions<GetRecruitmentListReturnValueType>,
    'queryKey' | 'queryFn'
  >;
} & Omit<IGetRecruitmentList, 'cookies'>;

export const useGetRecruitmentList = ({
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  page,
  limit,
  isShuffle,
  eventStart,
  eventEnd,
  entryStart,
  entryEnd,
  config,
}: GetRecruitmentListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecruitmentListReturnValueType>({
    ...(config ?? ({} as any)),
    queryKey: [
      'getRecruitmentList',
      order,
      companyIDs,
      industryCategoryIDs,
      groupedJobTypeIDs,
      gy,
      allgy,
      typeIDs,
      tagIDs,
      page,
      limit,
      isShuffle,
      eventStart,
      eventEnd,
      entryStart,
      entryEnd,
    ],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecruitmentList({
        cookies,
        order,
        companyIDs,
        industryCategoryIDs,
        groupedJobTypeIDs,
        gy,
        allgy,
        typeIDs,
        tagIDs,
        page,
        limit,
        isShuffle,
        eventStart,
        eventEnd,
        entryStart,
        entryEnd,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecruitmentDetail
 * GET /recruitment/${recruitmentID}/web
 */
type GetRecruitmentDetailArgs = {
  cookies: Partial<Record<string, string>>;
  recruitmentID: number;
  preview_code?: string;
  internal_preview_code?: string;
  sex?: number;
  bun_ri?: number;
  university?: number;
  current_univ_type?: InternalPreviewCodeCurrentUnivType;
  version?: string;
};

export type GetRecruitmentDetailReturnValueType = ResultWithOauthToken<
  RecruitmentDetailResponse,
  AppError
>;

export const getRecruitmentDetail = async ({
  cookies,
  recruitmentID,
  preview_code,
  internal_preview_code,
  sex,
  bun_ri,
  university,
  current_univ_type,
  version = DEFAULT_API_VERSION,
}: GetRecruitmentDetailArgs): Promise<GetRecruitmentDetailReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<GetRecruitmentDetailReturnValueType> => {
    defaultClient.setToken(accessToken);

    const params: {
      preview_code?: string;
      internal_preview_code?: string;
      sex?: number;
      bun_ri?: number;
      university?: number;
      current_univ_type?: string;
    } = filterExistParams({
      preview_code,
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
    });

    if (
      accessToken &&
      Object.hasOwn(params, 'preview_code') &&
      params.preview_code
    ) {
      // エラー画面表示のため、preview_codeの指定があってログイン状態の場合はForbiddenErrorとして返す、throwはしない
      return {
        ok: false,
        error: ForbiddenError,
      };
    }

    const path = createPathWithQuery(
      `/${version}/recruitment/${recruitmentID}/web`,
      params,
    );

    return defaultClient.get(path, RecruitmentDetailResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
    allowReturnError: true,
  });

  if (!result.ok) {
    if (preview_code) {
      // エラー画面表示のため、preview_codeの指定があってエラーの場合はすべてBadRequestErrorとして返す、throwはしない
      return {
        ok: false,
        error: BadRequestError,
      };
    }

    if (result.error === NotFoundError) {
      return {
        ok: false,
        error: NotFoundError,
      };
    }

    return {
      ok: false,
      error: NetworkError,
    };
  }

  return result;
};

/**
 * GetRecruitmentWebSeminars
 * v3.0
 */
type GetRecruitmentWebSeminarsArgs = {
  cookies: Partial<Record<string, string>>;
  companyId: number;
  limit: number;
  version?: string;
};

type GetRecruitmentWebSeminarsReturnValueType = ResultWithOauthToken<
  GetRecruitmentWebSeminarsResponse,
  AppError
>;

export const getRecruitmentWebSeminars = async ({
  cookies,
  companyId,
  limit,
  version = DEFAULT_API_VERSION,
}: GetRecruitmentWebSeminarsArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/web-seminars/${companyId}`,
      {limit},
    );

    return defaultClient.get(path, GetRecruitmentWebSeminarsResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetRecruitmentWebSeminarsParams = Omit<
  GetRecruitmentWebSeminarsArgs,
  'cookies'
> & {
  config?: Omit<
    UseQueryOptions<GetRecruitmentWebSeminarsReturnValueType>,
    'queryKey' | 'queryFn'
  >;
};

export const useGetRecruitmentWebSeminars = ({
  companyId,
  limit,
  version,
  config,
}: UseGetRecruitmentWebSeminarsParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecruitmentWebSeminarsReturnValueType>({
    ...(config ?? ({} as any)),
    queryKey: ['getRecruitmentWebSeminars', companyId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecruitmentWebSeminars({
        cookies,
        companyId,
        limit,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetSimilarRecruitmentList
 */
type GetSimilarRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  recruitmentDetailId: number; // 募集情報詳細URL末尾のID
  numberOfMatches?: number;
  numberOfRandom?: number;
  allowDuplicateCompany?: boolean;
  version?: string;
};

type GetSimilarRecruitmentListReturnValueType = ResultWithOauthToken<
  GetSimilarRecruitmentListResponse,
  AppError
>;

export const GetSimilarRecruitmentList = async ({
  cookies,
  recruitmentDetailId,
  version = DEFAULT_API_VERSION,
}: GetSimilarRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/${recruitmentDetailId}/similarities`,
      {},
    );

    return defaultClient.get(path, GetSimilarRecruitmentListResponseScheme);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetSimilarRecruitmentListParams = Omit<
  GetSimilarRecruitmentListArgs,
  'cookies'
> & {
  config?: Omit<
    UseQueryOptions<GetSimilarRecruitmentListReturnValueType>,
    'queryKey' | 'queryFn'
  >;
};

export const useGetSimilarRecruitmentList = ({
  recruitmentDetailId,
  version,
  config,
}: UseGetSimilarRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetSimilarRecruitmentListReturnValueType>({
    ...(config ?? ({} as any)),
    queryKey: ['getSimilarRecruitmentList', recruitmentDetailId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetSimilarRecruitmentList({
        cookies,
        recruitmentDetailId,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecommendedRecruitmentList
 */
type GetRecommendedRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetRecommendedRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecommendedRecruitmentListResponse,
  AppError
>;

export const GetRecommendedRecruitmentList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetRecommendedRecruitmentListArgs): Promise<GetRecommendedRecruitmentListReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecommendedRecruitmentListResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    // エンドポイントは未ログインを許容するが仕様と異なるため、accessTokenは必須とする

    return defaultClient.get(
      `/${version}/recruitment/recommends`,
      GetRecommendedRecruitmentListResponseSchema,
    );
  };

  const result = await sendApiRequest<GetRecommendedRecruitmentListResponse>({
    request,
    cookies,
    version,
  });

  return result;
};

type UseGetRecommendedRecruitmentListParams = {
  version?: string;
  config?: UseQueryOptions<GetRecommendedRecruitmentListReturnValueType>;
};

export const useGetRecommendedRecruitmentList = ({
  version,
  config,
}: UseGetRecommendedRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecommendedRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getRecommendedRecruitmentList'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetRecommendedRecruitmentList({
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRelatedCompanyRecruitmentList
 * エンドポイント修正中のためresponseShapeErrorになります
 */

type GetRelatedCompanyRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  companyID: number;
  version?: string;
};

type GetRelatedCompanyRecruitmentListReturnValueType = ResultWithOauthToken<
  RelatedCompanyRecruitmentListResponse,
  AppError
>;

export const GetRelatedCompanyRecruitmentList = async ({
  cookies,
  companyID,
  version = DEFAULT_API_VERSION,
}: GetRelatedCompanyRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/related-companies/${companyID}`,
      {},
    );

    return defaultClient.get(path, RelatedCompanyRecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetRelatedCompanyRecruitmentListParams = Omit<
  GetRelatedCompanyRecruitmentListArgs,
  'cookies'
> & {
  config?: Omit<
    UseQueryOptions<GetRelatedCompanyRecruitmentListReturnValueType>,
    'queryKey' | 'queryFn'
  >;
};

export const useGetRelatedCompanyRecruitmentList = ({
  companyID,
  version,
  config,
}: UseGetRelatedCompanyRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRelatedCompanyRecruitmentListReturnValueType>({
    ...(config ?? ({} as any)),
    queryKey: ['getRelatedCompanyRecruitmentList', companyID],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetRelatedCompanyRecruitmentList({
        cookies,
        companyID,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type ResultGetRecommendedRecruitsByIndustry = ResultWithOauthToken<
  GetRecommendedRecruitsByIndustryResponse,
  AppError
>;

type GetRecommendedRecruitsByIndustryArgs = {
  industryId: number;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getRecommendedRecruitsByIndustry = async ({
  industryId,
  cookies,
  version = DEFAULT_API_VERSION,
}: GetRecommendedRecruitsByIndustryArgs): Promise<ResultGetRecommendedRecruitsByIndustry> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecommendedRecruitsByIndustryResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/industries/${industryId}/recommended-recruitments`;
    return defaultClient.get(path, GetRecommendedRecruitsByIndustrySchema);
  };

  const result = await sendApiRequest<GetRecommendedRecruitsByIndustryResponse>(
    {
      request,
      cookies,
      version,
    },
  );

  return result;
};

type UseGetRecommendedRecruitsByIndustryOptions = {
  config?: UseQueryOptions<ResultGetRecommendedRecruitsByIndustry>;
} & Omit<GetRecommendedRecruitsByIndustryArgs, 'cookies'>;

export const useGetRecommendedRecruitsByIndustry = ({
  industryId,
  version = DEFAULT_API_VERSION,
  config,
}: UseGetRecommendedRecruitsByIndustryOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetRecommendedRecruitsByIndustry>({
    ...config,
    queryKey: ['getRecommendedRecruitsByIndustry', industryId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecommendedRecruitsByIndustry({
        industryId,
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetEntrySimilarRecruitmentList
 * users/me/entries/:entryID/recruitment-similarities
 * アーカイブ募集の/similaritiesとは違う
 */
type GetEntrySimilarRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  entryID: number; // エントリーID
  numberOfMatches?: number;
  numberOfRandom?: number;
  allowDuplicateCompany?: boolean;
  version?: string;
};

type GetEntrySimilarRecruitmentListReturnValueType = ResultWithOauthToken<
  GetEntrySimilarRecruitmentListResponse,
  AppError
>;

export const GetEntrySimilarRecruitmentList = async ({
  cookies,
  entryID,
  version = DEFAULT_API_VERSION,
}: GetEntrySimilarRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/users/me/entries/${entryID}/recruitment-similarities`,
      {},
    );

    return defaultClient.get(
      path,
      GetEntrySimilarRecruitmentListResponseScheme,
    );
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetEntrySimilarRecruitmentListParams = Omit<
  GetEntrySimilarRecruitmentListArgs,
  'cookies'
> & {
  config?: Omit<
    UseQueryOptions<GetEntrySimilarRecruitmentListReturnValueType>,
    'queryKey' | 'queryFn'
  >;
};

export const useGetEntrySimilarRecruitmentList = ({
  entryID,
  version,
  config,
}: UseGetEntrySimilarRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetEntrySimilarRecruitmentListReturnValueType>({
    ...(config ?? ({} as any)),
    queryKey: ['getEntrySimilarRecruitmentList', entryID],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetEntrySimilarRecruitmentList({
        cookies,
        entryID,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecommendRecruitmentList
 * v3.0/feed/recommend-recruitment
 */
type GetRecommendRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  limit?: number;
};

type GetRecommendRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecommendRecruitmentListResponse,
  AppError
>;

export const getRecommendRecruitmentList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  limit,
}: GetRecommendRecruitmentListArgs): Promise<GetRecommendRecruitmentListReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetRecommendRecruitmentListResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(`/${version}/feed/recommend-recruitment`, {
      limit,
    });
    return defaultClient.get(path, GetRecommendRecruitmentListResponseSchema);
  };

  const result = await sendApiRequest<GetRecommendRecruitmentListResponse>({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });

  return result;
};

type UseRecommendRecruitmentListOptions = {
  config?: UseQueryOptions<GetRecommendRecruitmentListReturnValueType>;
} & Omit<GetRecommendRecruitmentListArgs, 'cookies'>;

export const useRecommendRecruitmentList = ({
  version = DEFAULT_API_VERSION,
  limit,
  config,
}: UseRecommendRecruitmentListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecommendRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getRecommendRecruitmentList', version, limit],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecommendRecruitmentList({
        cookies,
        version,
        limit,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetBrowsingHistoryRecommendList
 * v3.0/recruitment/browsing-history-recommends
 */
type GetBrowsingHistoryRecommendListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  limit?: number;
};

type GetBrowsingHistoryRecommendListReturnValueType = ResultWithOauthToken<
  GetBrowsingHistoryRecommendListResponse,
  AppError
>;

export const getBrowsingHistoryRecommendList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  limit,
}: GetBrowsingHistoryRecommendListArgs): Promise<GetBrowsingHistoryRecommendListReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetBrowsingHistoryRecommendListResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(
      `/${version}/recruitment/browsing-history-recommends`,
      {
        limit,
      },
    );
    return defaultClient.get(
      path,
      GetBrowsingHistoryRecommendListResponseSchema,
    );
  };

  const result = await sendApiRequest<GetBrowsingHistoryRecommendListResponse>({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });

  return result;
};

type UseBrowsingHistoryRecommendListOptions = {
  config?: UseQueryOptions<GetBrowsingHistoryRecommendListReturnValueType>;
} & Omit<GetBrowsingHistoryRecommendListArgs, 'cookies'>;

export const useBrowsingHistoryRecommendList = ({
  version = DEFAULT_API_VERSION,
  limit,
  config,
}: UseBrowsingHistoryRecommendListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetBrowsingHistoryRecommendListReturnValueType>({
    ...config,
    queryKey: ['getBrowsingHistoryRecommendList', version, limit],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getBrowsingHistoryRecommendList({
        cookies,
        version,
        limit,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetEntryReadyRecommends
 * すぐエントリーできるおすすめ募集の取得（締切が近い順に最大3件）
 * GET /3.0/users/me/entry-ready-recommends
 */
type GetEntryReadyRecommendsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetEntryReadyRecommendsReturnValueType = ResultWithOauthToken<
  GetEntryReadyRecommendsResponse,
  AppError
>;

export const getEntryReadyRecommends = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetEntryReadyRecommendsArgs): Promise<GetEntryReadyRecommendsReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetEntryReadyRecommendsResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/users/me/entry-ready-recommends`,
      GetEntryReadyRecommendsResponseSchema,
    );
  };

  return sendApiRequest<GetEntryReadyRecommendsResponse>({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });
};

type UseGetEntryReadyRecommendsOptions = {
  config?: UseQueryOptions<GetEntryReadyRecommendsReturnValueType>;
} & Omit<GetEntryReadyRecommendsArgs, 'cookies'>;

export const useGetEntryReadyRecommends = ({
  version = DEFAULT_API_VERSION,
  config,
}: UseGetEntryReadyRecommendsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetEntryReadyRecommendsReturnValueType>({
    // 対象外のユーザーでも画面遷移ごとに叩かれるため、セッション中は使い回す
    staleTime: 5 * 60 * 1000,
    ...config,
    queryKey: ['getEntryReadyRecommends', version],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntryReadyRecommends({cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetUnfinishedEntryPrompt
 * エントリーボタンを押したが未完了で、締切が近い募集の取得（締切が近い順、同じなら最後にクリックした順に最大3件）
 * GET /3.0/users/me/unfinished-entry-prompt
 */
type GetUnfinishedEntryPromptArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetUnfinishedEntryPromptReturnValueType = ResultWithOauthToken<
  GetUnfinishedEntryPromptResponse,
  AppError
>;

export const getUnfinishedEntryPrompt = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetUnfinishedEntryPromptArgs): Promise<GetUnfinishedEntryPromptReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetUnfinishedEntryPromptResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/users/me/unfinished-entry-prompt`,
      GetUnfinishedEntryPromptResponseSchema,
    );
  };

  return sendApiRequest<GetUnfinishedEntryPromptResponse>({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });
};

type UseGetUnfinishedEntryPromptOptions = {
  // アカウントを切り替えたとき前のユーザーのキャッシュを使わないよう、キーに含める
  userId: number | undefined;
  config?: UseQueryOptions<GetUnfinishedEntryPromptReturnValueType>;
} & Omit<GetUnfinishedEntryPromptArgs, 'cookies'>;

export const useGetUnfinishedEntryPrompt = ({
  userId,
  version = DEFAULT_API_VERSION,
  config,
}: UseGetUnfinishedEntryPromptOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetUnfinishedEntryPromptReturnValueType>({
    // 対象がないユーザーでもホームに戻るたびに叩かれるため短時間は使い回す。
    // 古いキャッシュのまま表示を判定しないよう、呼び出し側は再取得中の結果を使わない
    staleTime: 5 * 60 * 1000,
    ...config,
    queryKey: ['getUnfinishedEntryPrompt', version, userId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getUnfinishedEntryPrompt({cookies, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetEntrySimilarRecommends
 * エントリー完了直後に勧める、エントリーした募集に似た募集の取得（最大3件）
 * GET /3.0/users/me/entries/:recruitmentId/similar-recruitment-recommends
 * recruitmentId はエントリーした募集の recruitments.id（募集情報 ID ではない）
 */
type GetEntrySimilarRecommendsArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  recruitmentId: number;
};

type GetEntrySimilarRecommendsReturnValueType = ResultWithOauthToken<
  GetEntrySimilarRecommendsResponse,
  AppError
>;

export const getEntrySimilarRecommends = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  recruitmentId,
}: GetEntrySimilarRecommendsArgs): Promise<GetEntrySimilarRecommendsReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetEntrySimilarRecommendsResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entries/${recruitmentId}/similar-recruitment-recommends`;
    return defaultClient.get(path, GetEntrySimilarRecommendsResponseSchema);
  };

  return sendApiRequest<GetEntrySimilarRecommendsResponse>({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });
};

type UseGetEntrySimilarRecommendsOptions = {
  config?: UseQueryOptions<GetEntrySimilarRecommendsReturnValueType>;
} & Omit<GetEntrySimilarRecommendsArgs, 'cookies'>;

export const useGetEntrySimilarRecommends = ({
  version = DEFAULT_API_VERSION,
  recruitmentId,
  config,
}: UseGetEntrySimilarRecommendsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetEntrySimilarRecommendsReturnValueType>({
    ...config,
    queryKey: ['getEntrySimilarRecommends', version, recruitmentId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntrySimilarRecommends({
        cookies,
        version,
        recruitmentId,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetQuickEntryRecruitmentList
 * 質問なしエントリーフォームの募集一覧取得
 * GET /service/recruitment/quick-entry
 */
type GetQuickEntryRecruitmentListParam = {
  order?: GetRecruitmentOrderType;
  companyIDs?: number[];
  industryCategoryIDs?: number[];
  groupedJobTypeIDs?: number[];
  gy?: number;
  allgy?: boolean;
  typeIDs?: number[];
  tagIDs?: GetRecruitmentTagsType[];
  page?: number;
  limit?: number;
  isShuffle?: boolean;
  excludeEngineer?: boolean;
};
export type IGetQuickEntryRecruitmentList =
  GetQuickEntryRecruitmentListParam & {
    cookies: Partial<Record<string, string>>;
    version?: string;
  };

export type GetQuickEntryRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecruitmentListResponse,
  AppError
>;

export const getQuickEntryRecruitmentList = async ({
  cookies,
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  page,
  limit,
  isShuffle,
  excludeEngineer,
  version = DEFAULT_API_VERSION,
}: IGetQuickEntryRecruitmentList): Promise<GetQuickEntryRecruitmentListReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/service/recruitment/quick-entry`,
      {
        order: order ?? RECRUITMENT_ORDER.RECOMMEND,
        companyIDs: companyIDs ?? '',
        industryCategoryIDs: industryCategoryIDs ?? '',
        groupedJobTypeIDs: groupedJobTypeIDs ?? '',
        gy,
        allgy: allgy ?? false,
        typeIDs: typeIDs ?? '',
        tagIDs: tagIDs ?? '',
        page: page ?? 1,
        limit: limit ?? 10,
        isShuffle: isShuffle ?? false,
        excludeEngineer,
      },
      'none',
    );
    return defaultClient.get(path, RecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetQuickEntryRecruitmentListOptions = {
  config?: Omit<
    UseInfiniteQueryOptions<GetQuickEntryRecruitmentListReturnValueType>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >;
} & Omit<IGetQuickEntryRecruitmentList, 'cookies' | 'page'>;

export const useGetQuickEntryRecruitmentList = ({
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  limit,
  isShuffle,
  excludeEngineer,
  version,
  config,
}: UseGetQuickEntryRecruitmentListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useInfiniteQuery<GetQuickEntryRecruitmentListReturnValueType>({
    ...(config ?? {}),
    queryKey: [
      'getQuickEntryRecruitmentList',
      version,
      order,
      companyIDs,
      industryCategoryIDs,
      groupedJobTypeIDs,
      gy,
      allgy,
      typeIDs,
      tagIDs,
      limit,
      isShuffle,
      excludeEngineer,
    ],
    async queryFn({pageParam = 1}) {
      const cookies = parseCookies();
      const result = await getQuickEntryRecruitmentList({
        cookies,
        order,
        companyIDs,
        industryCategoryIDs,
        groupedJobTypeIDs,
        gy,
        allgy,
        typeIDs,
        tagIDs,
        page: pageParam,
        limit,
        isShuffle,
        excludeEngineer,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.ok) return undefined;
      // nextPage が 0 のときは次ページなしとして終端する
      return lastPage.value.nextPage || undefined;
    },
  });
};
